<script setup lang="ts">
  import { useFormFieldProvider } from './gat-form-field.vue'
  import { IconError, IconWarning, IconInfo } from '../icons'
  import { GatIcon } from '../gat-icon'
  import { computed, ref } from 'vue'

  const { hasAnyMessages, messages } = useFormFieldProvider()

  const errorMessages = computed(() => {
    return messages.value.filter((message) => message.variant === 'error')
  })

  const warningMessages = computed(() => {
    return messages.value.filter((message) => message.variant === 'warning')
  })

  const infoMessages = computed(() => {
    return messages.value.filter((message) => message.variant === 'info')
  })

  const canShowMore = computed(() => {
    if (errorMessages.value.length > 0) {
      return errorMessages.value.some(
        (message) => message.messageLong && message.messageLong.length > 0
      )
    }
    if (warningMessages.value.length > 0) {
      return warningMessages.value.some(
        (message) => message.messageLong && message.messageLong.length > 0
      )
    }
    if (infoMessages.value.length > 0) {
      return infoMessages.value.some(
        (message) => message.messageLong && message.messageLong.length > 0
      )
    }
    return false
  })

  const longMessages = computed(() => {
    return messages.value.filter(
      (message) => message.messageLong && message.messageLong.length > 0
    )
  })

  const showMore = ref(false)
</script>

<template>
  <div
    v-if="hasAnyMessages"
    class="bg-background-lighter flex w-full flex-col gap-2 p-2"
  >
    <div class="text-foreground inline-flex w-full items-center gap-2 text-xs">
      <slot v-if="errorMessages.length > 0">
        <icon-error class="size-4" />
        <span class="whitespace-pre-line">
          <slot name="message-short" v-bind="{ messages: errorMessages }">
            {{
              errorMessages.map((message) => message.message).join('\r\n\r\n')
            }}
          </slot>
        </span>
      </slot>
      <slot v-else-if="warningMessages.length > 0">
        <icon-warning class="size-5" />
        <span class="whitespace-pre-line">
          <slot name="message-short" v-bind="{ messages: warningMessages }">
            {{
              warningMessages.map((message) => message.message).join('\r\n\r\n')
            }}
          </slot>
        </span>
      </slot>
      <slot v-else-if="infoMessages.length > 0">
        <icon-info class="size-4" />

        <span class="whitespace-pre-line">
          <slot name="message-short" v-bind="{ messages: infoMessages }">
            {{
              infoMessages.map((message) => message.message).join('\r\n\r\n')
            }}
          </slot>
        </span>
      </slot>
      <div
        v-if="canShowMore"
        class="text-soft hover:text-foreground ml-auto flex cursor-pointer items-center gap-0.5"
        @click="showMore = !showMore"
      >
        <span class="text-xs">{{ showMore ? 'show less' : 'show more' }}</span>
        <gat-icon
          icon="ChevronDown"
          class="size-4"
          :class="{ 'rotate-180': showMore }"
        />
      </div>
    </div>

    <div
      :class="{
        hidden: !showMore,
        block: showMore
      }"
    >
      <slot name="messages-long" :messages="messages">
        <div v-for="message in longMessages" :key="message.message">
          <div
            v-if="typeof message.messageLong === 'string'"
            class="whitespace-pre-line text-xs"
          >
            {{ message.messageLong }}
          </div>
          <component v-else :is="message.messageLong" />
        </div>
      </slot>
    </div>
  </div>
</template>
